import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { scrollToId } from "@/lib/scroll";

const LINKS = [
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Projects", href: "#projects" },
  { label: "Process", href: "#process" },
  { label: "Contact", href: "#contact" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    setTimeout(() => scrollToId(href), open ? 250 : 0);
  };

  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled
            ? "border-b border-[#C5A059]/15 bg-[#050B14]/85 backdrop-blur-xl"
            : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8">
          <button onClick={() => go("#home")} aria-label="Tyrell Property Services — home" data-testid="nav-logo-button">
            <Logo />
          </button>
          <nav className="hidden items-center gap-9 lg:flex" aria-label="Primary">
            {LINKS.map((l) => (
              <button
                key={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                onClick={() => go(l.href)}
                className="group relative text-[11px] font-medium uppercase tracking-[0.25em] text-slate-300 transition-colors duration-300 hover:text-white"
              >
                {l.label}
                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-[#C5A059] transition-all duration-300 group-hover:w-full" />
              </button>
            ))}
            <button
              data-testid="nav-quote-button"
              onClick={() => go("#contact")}
              className="border border-[#C5A059]/60 px-6 py-2.5 text-[11px] font-semibold uppercase tracking-[0.25em] text-[#C5A059] transition-all duration-300 hover:bg-[#C5A059] hover:text-[#050B14]"
            >
              Request a Quote
            </button>
          </nav>
          <button
            data-testid="nav-menu-toggle"
            className="text-white lg:hidden"
            onClick={() => setOpen(!open)}
            aria-label={open ? "Close menu" : "Open menu"}
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 flex flex-col items-center justify-center gap-2 bg-[#050B14]/97 backdrop-blur-2xl lg:hidden"
          >
            {LINKS.map((l, i) => (
              <motion.button
                key={l.href}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 * i, duration: 0.5 }}
                onClick={() => go(l.href)}
                className="py-3 font-serif text-3xl font-light uppercase tracking-[0.15em] text-white transition-colors hover:text-[#C5A059]"
              >
                {l.label}
              </motion.button>
            ))}
            <motion.button
              data-testid="mobile-nav-quote-button"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45, duration: 0.5 }}
              onClick={() => go("#contact")}
              className="mt-6 border border-[#C5A059] px-10 py-3.5 text-xs font-semibold uppercase tracking-[0.3em] text-[#C5A059]"
            >
              Request a Quote
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
